import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { supabase, isAuthConfigured } from '@/lib/supabase'
import { API_BASE_URL } from '@/lib/api/core'
import { createCursorToken } from '@/lib/api/tokens'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'

type BridgePhase = 'loading' | 'needs_login' | 'completing' | 'error' | 'done'

/** Paper tokens from landing prototype / maater-design-ui (inline for public connect route). */
const paperStyle = {
  '--paper': 'oklch(97% 0.012 75)',
  '--paper-raised': 'oklch(93.5% 0.02 72)',
  '--ink': 'oklch(24% 0.03 50)',
  '--ink-soft': 'oklch(42% 0.025 52)',
  '--ink-faint': 'oklch(58% 0.02 55)',
  '--line': 'oklch(85% 0.02 68)',
  '--rust': 'oklch(56% 0.17 35)',
  '--cream-card': 'oklch(98.5% 0.008 80)',
} as CSSProperties

function isAllowedCallback(callback: string): boolean {
  if (callback.startsWith('vscode://') || callback.startsWith('cursor://')) return true
  try {
    const u = new URL(callback)
    return u.protocol === 'http:' && (u.hostname === '127.0.0.1' || u.hostname === 'localhost')
  } catch {
    return false
  }
}

/**
 * Peel percent-encoding that baked `?windowId=` into the path.
 * TJ WSL: callback arrived as cursor://flownotes.cursor/auth%3FwindowId%3D4
 * (browser bar showed auth%253F after encodeURIComponent of that).
 */
function normalizeCursorCallback(callback: string): string {
  let s = callback.trim()
  for (let i = 0; i < 3; i++) {
    if (!/%[0-9A-Fa-f]{2}/.test(s)) break
    try {
      const next = decodeURIComponent(s)
      if (next === s) break
      s = next
    } catch {
      break
    }
  }
  return s
}

/**
 * Append query params without `new URL(callback)` - custom schemes (cursor:// / vscode://)
 * are parsed inconsistently across browsers and WSL protocol handoff, sometimes baking
 * `?windowId=` into the path so the extension sees route "auth?windowId=4".
 */
function appendCallbackParams(callback: string, params: Record<string, string>): string {
  const normalized = normalizeCursorCallback(callback)
  const hashIdx = normalized.indexOf('#')
  const withoutHash = hashIdx === -1 ? normalized : normalized.slice(0, hashIdx)
  const hash = hashIdx === -1 ? '' : normalized.slice(hashIdx)
  const join = withoutHash.includes('?') ? '&' : '?'
  const qs = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
  return `${withoutHash}${join}${qs}${hash}`
}

/**
 * SPA connect bridge for Cursor extension and maater-sync CLI.
 * Uses Supabase session on the webapp origin, then redirects with PAT.
 */
export function ConnectCursorPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<BridgePhase>('loading')
  const [error, setError] = useState<string | null>(null)
  const finishStarted = useRef(false)

  const callback = searchParams.get('callback') || ''
  const isCliCallback =
    callback.startsWith('http://127.0.0.1') || callback.startsWith('http://localhost')

  const finishConnect = useCallback(async () => {
    if (!callback || !isAllowedCallback(callback)) {
      setPhase('error')
      setError('Missing or invalid callback parameter.')
      return
    }

    if (!isAuthConfigured || !supabase) {
      setPhase('error')
      setError('Sign-in is not configured for this deployment.')
      return
    }

    const { data: sessionResult } = await supabase.auth.getSession()
    const session = sessionResult.session
    if (!session) {
      setPhase('needs_login')
      const next = `/connect/cursor?${searchParams.toString()}`
      navigate(`/login?redirect=${encodeURIComponent(next)}`, { replace: true })
      return
    }

    setPhase('completing')

    try {
      const tokenResponse = await createCursorToken({
        name: `Local sync ${new Date().toISOString().split('T')[0]}`,
      })

      const redirectHref = appendCallbackParams(callback, {
        token: tokenResponse.token,
        api: API_BASE_URL.replace(/\/$/, ''),
      })

      setPhase('done')
      window.location.href = redirectHref
    } catch (err: unknown) {
      setPhase('error')
      setError(err instanceof Error ? err.message : 'Failed to create sync token')
    }
  }, [callback, navigate, searchParams])

  useEffect(() => {
    if (finishStarted.current) return
    finishStarted.current = true
    finishConnect().catch(() => {
      setPhase('error')
      setError('Unexpected error completing connection.')
    })
  }, [finishConnect])

  const retry = () => {
    finishStarted.current = false
    setError(null)
    setPhase('loading')
    finishStarted.current = true
    finishConnect().catch(() => {
      setPhase('error')
      setError('Unexpected error completing connection.')
    })
  }

  const returnHint = isCliCallback
    ? 'Return to your terminal when this finishes.'
    : 'Return to Cursor when this tab closes.'

  return (
    <div
      className="flex min-h-screen items-center justify-center p-6"
      data-testid={testIds.oauth.connectCursorBridge}
      style={{
        ...paperStyle,
        background:
          'radial-gradient(120% 80% at 12% -10%, color-mix(in oklch, var(--paper) 70%, white) 0%, transparent 55%), var(--paper)',
        color: 'var(--ink)',
      }}
    >
      <div
        className="w-full max-w-md space-y-4 rounded-xl border p-8 text-center shadow-sm"
        style={{
          background: 'var(--cream-card)',
          borderColor: 'var(--line)',
        }}
      >
        <p className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Georgia, ui-serif, serif' }}>
          M<span style={{ color: 'var(--rust)' }}>aa</span>ter
        </p>
        <h1 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>
          Connect sync
        </h1>
        {phase === 'needs_login' ? (
          <p className="text-sm" style={{ color: 'var(--ink-soft)' }}>
            Redirecting to sign in...
          </p>
        ) : phase === 'completing' || phase === 'loading' || phase === 'done' ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--rust)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-soft)' }}>
              {phase === 'done' ? 'Redirecting...' : 'Authorizing this device...'}
            </p>
            <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
              {returnHint}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-destructive">{error}</p>
            <Button type="button" variant="outline" onClick={retry}>
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
